
import React from "react";

// ========================================
// PRODUCT FAQs
// ========================================

const ProductFAQs = ({
    data = [],
    onChange
}) => {

    const faqs = Array.isArray(data)
        ? data
        : [];

    // ========================================
    // STYLES
    // ========================================

    const inputClass =
        "w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-300";

    const labelClass =
        "block text-sm font-medium text-gray-700 mb-2";

    const addButtonClass =
        "px-4 py-2 bg-gray-800 text-white text-sm rounded hover:bg-gray-700";

    const removeButtonClass =
        "px-3 py-2 bg-red-50 text-red-700 border border-red-200 text-sm rounded hover:bg-red-100";

    // ========================================
    // FAQ MANAGEMENT
    // ========================================

    const addFAQ = () => {

        onChange([
            ...faqs,
            {
                question: "",
                answer: "",
                keywords: []
            }
        ]);

    };

    const updateFAQ = (
        index,
        field,
        value
    ) => {

        onChange(
            faqs.map((faq, i) =>
                i === index
                    ? {
                        ...faq,
                        [field]: value
                    }
                    : faq
            )
        );

    };

    const removeFAQ = (index) => {

        onChange(
            faqs.filter((_, i) => i !== index)
        );

    };

    // ========================================
    // KEYWORD MANAGEMENT
    // ========================================

    const addKeyword = (faqIndex) => {

        const faq = faqs[faqIndex];

        updateFAQ(
            faqIndex,
            "keywords",
            [
                ...(faq.keywords || []),
                ""
            ]
        );

    };

    const updateKeyword = (
        faqIndex,
        keywordIndex,
        value
    ) => {

        const faq = faqs[faqIndex];

        const updatedKeywords = (faq.keywords || []).map(
            (keyword, index) =>
                index === keywordIndex
                    ? value
                    : keyword
        );

        updateFAQ(
            faqIndex,
            "keywords",
            updatedKeywords
        );

    };

    const removeKeyword = (
        faqIndex,
        keywordIndex
    ) => {

        const faq = faqs[faqIndex];

        updateFAQ(
            faqIndex,
            "keywords",
            (faq.keywords || []).filter(
                (_, index) => index !== keywordIndex
            )
        );

    };

    // ========================================
    // RENDER
    // ========================================

    return (

        <div className="space-y-6">

            {/* HEADER */}

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 border-b pb-4">

                <div>

                    <h3 className="text-base font-semibold text-gray-900">
                        Frequently Asked Questions
                    </h3>

                    <p className="text-sm text-gray-500 mt-1">
                        Add common customer questions, approved
                        answers and relevant keywords to improve
                        product knowledge retrieval.
                    </p>

                </div>

                <button
                    type="button"
                    onClick={addFAQ}
                    className={addButtonClass}
                >
                    + Add FAQ
                </button>

            </div>

            {/* EMPTY STATE */}

            {faqs.length === 0 && (

                <div className="py-12 text-center border border-dashed rounded-lg">

                    <p className="text-sm text-gray-500">
                        No FAQs added yet.
                    </p>

                    <p className="text-xs text-gray-400 mt-2">
                        Add frequently asked questions
                        about this product.
                    </p>

                </div>

            )}

            {/* FAQ LIST */}

            {faqs.map((faq, faqIndex) => (

                <div
                    key={faq._id || faqIndex}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                >

                    {/* FAQ HEADER */}

                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 bg-gray-100 px-5 py-4">

                        <div>

                            <h4 className="font-semibold text-gray-900">
                                FAQ {faqIndex + 1}
                            </h4>

                            {faq.question && (

                                <p className="text-xs text-gray-500 mt-1">
                                    {faq.question}
                                </p>

                            )}

                        </div>

                        <button
                            type="button"
                            onClick={() =>
                                removeFAQ(faqIndex)
                            }
                            className={removeButtonClass}
                        >
                            Remove FAQ
                        </button>

                    </div>

                    {/* FAQ FIELDS */}

                    <div className="p-5 space-y-5">

                        {/* QUESTION */}

                        <div>

                            <label className={labelClass}>
                                Question *
                            </label>

                            <textarea
                                value={faq.question || ""}
                                onChange={(event) =>
                                    updateFAQ(
                                        faqIndex,
                                        "question",
                                        event.target.value
                                    )
                                }
                                placeholder="e.g. Can this product be applied on an existing concrete surface?"
                                rows={3}
                                className={inputClass}
                            />

                        </div>

                        {/* ANSWER */}

                        <div>

                            <label className={labelClass}>
                                Answer *
                            </label>

                            <textarea
                                value={faq.answer || ""}
                                onChange={(event) =>
                                    updateFAQ(
                                        faqIndex,
                                        "answer",
                                        event.target.value
                                    )
                                }
                                placeholder="Enter the approved answer based on verified product documentation."
                                rows={5}
                                className={inputClass}
                            />

                        </div>

                        {/* KEYWORDS */}

                        <div className="border-t pt-5 space-y-4">

                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">

                                <div>

                                    <h5 className="text-sm font-semibold text-gray-800">
                                        Search Keywords
                                    </h5>

                                    <p className="text-xs text-gray-500 mt-1">
                                        Add words or phrases customers
                                        might use when asking this question.
                                    </p>

                                </div>

                                <button
                                    type="button"
                                    onClick={() =>
                                        addKeyword(faqIndex)
                                    }
                                    className="text-sm text-blue-700 hover:underline"
                                >
                                    + Add Keyword
                                </button>

                            </div>

                            {(faq.keywords || []).length === 0 && (

                                <p className="text-xs text-gray-500">
                                    No keywords added.
                                </p>

                            )}

                            {(faq.keywords || []).map(
                                (keyword, keywordIndex) => (

                                    <div
                                        key={keywordIndex}
                                        className="flex flex-col sm:flex-row gap-3"
                                    >

                                        <div className="flex-1">

                                            <input
                                                type="text"
                                                value={keyword || ""}
                                                onChange={(event) =>
                                                    updateKeyword(
                                                        faqIndex,
                                                        keywordIndex,
                                                        event.target.value
                                                    )
                                                }
                                                placeholder="e.g. existing concrete, surface preparation"
                                                className={inputClass}
                                            />

                                        </div>

                                        <div className="sm:self-end">

                                            <button
                                                type="button"
                                                onClick={() =>
                                                    removeKeyword(
                                                        faqIndex,
                                                        keywordIndex
                                                    )
                                                }
                                                className={removeButtonClass}
                                            >
                                                Remove
                                            </button>

                                        </div>

                                    </div>

                                )
                            )}

                        </div>

                    </div>

                </div>

            ))}

            {/* GUIDANCE */}

            <div className="border-t pt-4">

                <p className="text-xs text-gray-500">
                    FAQ answers should be accurate, product-specific
                    and supported by approved information. Keywords
                    can assist knowledge retrieval, but the AI
                    agent should understand natural-language
                    variations rather than rely only on exact
                    keyword matches.
                </p>

            </div>

        </div>

    );

};

export default ProductFAQs;
